import { useState } from 'react'

export default function PriceCalculator({ onContactClick }) {
  const [step, setStep] = useState(1)
  const [area, setArea] = useState(30)
  const [tier, setTier] = useState('standard') // 'budget' | 'standard' | 'luksus'

  // Pricing rates per m2 (realistic Danish carpenter rates)
  const rates = {
    budget: 1250,   // Trykimprægneret fyr
    standard: 1950, // Lærketræ / Douglasgran
    luksus: 2950,   // Hårdttræ (Ipe/Cumaru) eller premium komposit
  }

  const tierDetails = {
    budget: {
      title: 'Budget',
      material: 'Trykimprægneret fyrretræ',
      desc: 'En økonomisk og klassisk løsning med god holdbarhed. Kræver løbende træbeskyttelse.',
      features: ['Klassisk grønligt/gyldent look', 'Budgetvenlig løsning', 'Holdbarhed ca. 10-15 år'],
    },
    standard: {
      title: 'Standard',
      material: 'Sibirsk lærketræ / Douglasgran',
      desc: 'Det mest populære valg. Flot, naturligt look med højt indhold af naturlige olier og syre.',
      features: ['Flot rødlig/gylden farve', 'Høj naturlig imprægnering', 'Holdbarhed ca. 15-20 år'],
    },
    luksus: {
      title: 'Luksus',
      material: 'Eksklusivt hårdttræ eller Premium Komposit',
      desc: 'Det ultimative valg for maksimal holdbarhed og minimal vedligeholdelse. Eksklusiv finish.',
      features: ['Ekstremt slidstærkt', 'Minimal vedligeholdelse (komposit)', 'Holdbarhed ca. 25-40 år'],
    },
  }

  const calculatePrice = () => {
    const basePrice = area * rates[tier]
    // Add a base setup fee for smaller areas to make it realistic
    const setupFee = area < 25 ? 5000 : 3000
    return basePrice + setupFee
  }

  const formattedPrice = new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  }).format(calculatePrice())

  return (
    <div className="bg-brand-sand p-6 md:p-8 rounded-xl border border-brand-sand-dark shadow-sm max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6 border-b border-brand-sand-dark pb-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-brand-blue">Prisberegner.online</span>
          <h3 className="text-2xl font-bold text-brand-dark">Beregn prisen på træterrasse</h3>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`h-2.5 w-2.5 rounded-full ${step >= 1 ? 'bg-brand-blue' : 'bg-slate-300'}`} />
          <div className={`h-2.5 w-2.5 rounded-full ${step >= 2 ? 'bg-brand-blue' : 'bg-slate-300'}`} />
          <div className={`h-2.5 w-2.5 rounded-full ${step >= 3 ? 'bg-brand-blue' : 'bg-slate-300'}`} />
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-bold text-brand-dark mb-2">Hvor mange kvadratmeter træterrasse ønskes anlagt?</h4>
            <p className="text-sm text-slate-600 mb-6">Træk i skyderen eller indtast det ønskede areal (mellem 10 og 200 m²).</p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-500">10 m²</span>
                <div className="flex items-baseline gap-1">
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={area}
                    onChange={(e) => setArea(Math.max(10, Math.min(200, Number(e.target.value))))}
                    className="w-20 px-2 py-1 text-center font-bold text-xl text-brand-dark border border-slate-300 rounded-lg focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none bg-white"
                  />
                  <span className="text-lg font-bold text-brand-dark">m²</span>
                </div>
                <span className="text-sm font-bold text-slate-500">200 m²</span>
              </div>

              <input
                type="range"
                min="10"
                max="200"
                value={area}
                onChange={(e) => setArea(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-blue"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={() => setStep(2)}
              className="bg-brand-blue hover:bg-brand-blue-hover text-white font-bold py-2.5 px-6 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
            >
              Næste trin
              <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-bold text-brand-dark mb-2">Vælg kvalitetstype og materiale</h4>
            <p className="text-sm text-slate-600 mb-6">Vælg den materialeklasse, der passer bedst til dit budget og dine ønsker.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(tierDetails).map(([key, details]) => (
                <button
                  key={key}
                  onClick={() => setTier(key)}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between h-full ${
                    tier === key
                      ? 'border-brand-blue bg-white ring-1 ring-brand-blue shadow-sm'
                      : 'border-slate-300 bg-white/50 hover:border-slate-400'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-brand-dark text-lg">{details.title}</span>
                      {tier === key && <i className="fa-solid fa-circle-check text-brand-blue"></i>}
                    </div>
                    <span className="text-xs font-semibold text-brand-blue block mb-2">{details.material}</span>
                    <p className="text-xs text-slate-600 leading-relaxed mb-4">{details.desc}</p>
                  </div>
                  <ul className="space-y-1.5 border-t border-slate-100 pt-3 w-full">
                    {details.features.map((f, idx) => (
                      <li key={idx} className="text-[10px] text-slate-500 flex items-center gap-1">
                        <i className="fa-solid fa-check text-brand-blue text-[8px]"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-brand-sand-dark">
            <button
              onClick={() => setStep(1)}
              className="border border-slate-300 hover:border-slate-400 text-slate-700 font-bold py-2.5 px-6 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
            >
              <i className="fa-solid fa-arrow-left"></i>
              Tilbage
            </button>
            <button
              onClick={() => setStep(3)}
              className="bg-brand-blue hover:bg-brand-blue-hover text-white font-bold py-2.5 px-6 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
            >
              Beregn pris
              <i className="fa-solid fa-calculator"></i>
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6 text-center py-4">
          <div>
            <i className="fa-solid fa-calculator text-5xl text-brand-blue mb-3"></i>
            <h4 className="text-xl font-bold text-brand-dark mb-1">Din prisberegning er klar!</h4>
            <p className="text-sm text-slate-600">Baseret på dine valg har vi beregnet et uforpligtende estimat.</p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-brand-sand-dark max-w-md mx-auto shadow-sm">
            <div className="space-y-2 text-left border-b border-slate-100 pb-4 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Areal:</span>
                <span className="font-bold text-brand-dark">{area} m²</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Type:</span>
                <span className="font-bold text-brand-dark">{tierDetails[tier].title} ({tierDetails[tier].material})</span>
              </div>
            </div>

            <div className="text-center">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Estimeret pris (inkl. moms & montering)</span>
              <span className="text-3xl md:text-4xl font-extrabold text-brand-orange block mb-2">{formattedPrice}</span>
              <span className="text-[10px] text-slate-400 block">Prisen er vejledende og inklusiv arbejdsløn, materialer og moms.</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
            <button
              onClick={() => setStep(2)}
              className="border border-slate-300 hover:border-slate-400 text-slate-700 font-bold py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="fa-solid fa-rotate-left"></i>
              Beregn igen
            </button>
            <button
              onClick={onContactClick}
              className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer shadow-sm"
            >
              <i className="fa-solid fa-envelope"></i>
              Få et præcist tilbud
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
